import { capitalize, isIn, isSome } from './util.js';
import { Maybe, UserError } from './data.js';

export interface HttpError {
	code: number;
	httpMessage: string;
	message?: string;
}

export const httpErrorNames = ['moved', 'found', 'seeOther', 'notFound', 'forbidden', 'unauthorized', 'badRequest', 'gone', 'serverError', 'badGateway', 'serviceUnavailable', 'gatewayTimeout'] as const;
export type HttpErrorName = (typeof httpErrorNames)[number];

export type HttpRedirectKind = 'moved' | 'found' | 'seeOther';

export const httpErrors: Record<HttpErrorName, HttpError> = {
	moved: {
		code: 301,
		httpMessage: 'Moved permanently',
	},
	found: {
		code: 302,
		httpMessage: 'Found',
	},
	seeOther: {
		code: 303,
		httpMessage: 'See Other',
	},
	notFound: {
		code: 404,
		httpMessage: 'Not found',
		message: 'The requested entity was not found.',
	},
	forbidden: {
		code: 403,
		httpMessage: 'Forbidden',
		message: 'Sorry, you have no sufficient access to view or edit this.',
	},
	unauthorized: {
		code: 401,
		httpMessage: 'Unauthorized',
		message: 'You need to log in to access this.',
	},
	badRequest: {
		code: 400,
		httpMessage: 'Bad request',
		message: 'The server refused to process the request. This may be a bug.',
	},
	gone: {
		code: 410,
		httpMessage: 'Gone',
		message: 'The requested entity has been deleted.',
	},
	serverError: {
		code: 500,
		httpMessage: 'Internal Server Error',
	},
	badGateway: {
		code: 502,
		httpMessage: 'Bad Gateway',
	},
	serviceUnavailable: {
		code: 503,
		httpMessage: 'Service Unavailable',
	},
	gatewayTimeout: {
		code: 504,
		httpMessage: 'Gateway Timeout',
	},
};

export interface ApiErrorData extends UserError {
	requestId?: Maybe<number>;
	code?: Maybe<number>;
	error?: Maybe<HttpErrorName | string>;
	message?: Maybe<string>;
}

export class ApiError extends Error implements ApiErrorData {
	
	data: ApiErrorData;
	requestId?: Maybe<number>;
	code = 400;
	error?: HttpErrorName | string;
	
	constructor(data: ApiErrorData | HttpErrorName, options?: ErrorOptions) {
		data = typeof data == 'string' ? { error: data } : data;
		super(data.message ?? (isIn(data.error, httpErrorNames) ? httpErrors[data.error].message : null) ?? data.error ?? 'Unknown API error', options);
		this.data = data;
		if (isSome(data.error)) this.name = `${capitalize(data.error)}Error`;
		Object.assign(this, data);
	}
	
	toJSON() {
		return {
			...this,
			...this.data,
		};
	}
	
	static message = (message: string) => new this({ message });
	static forbidden = (message: string) => new this({ error: 'forbidden', message });
	
}
